/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/**
 * Client for the medaica-service pcg_review endpoints. The authoritative
 * wire contract is the service implementation (`app/pcg_review/schemas.py`
 * and `export_validation.py`); pcg-review-types.ts mirrors it.
 *
 * Deliberate delta from the house pattern: no class-transformer models — the pcg-review DTOs are plain camelCase
 * types (pcg-review-types.ts) because their interesting members (guide
 * payload, export payload) are opaque JSON that pcg-core owns; ISO dates
 * are still revived to Date by the shared axios interceptor.
 */

import { AxiosError } from "axios"
import MedaicaApiService from "./"
import { Exam } from "@medaica/common/types"
import {
  PcgDispositionRequest,
  PcgExportRequest,
  PcgExportValidationErrorBody,
  PcgHeartReport,
  PcgHeartReportConflictBody,
  PcgHeartReportRelease,
  PcgHeartReportSaveRequest,
  PcgHeartReportSummary,
  PcgRecordingDisposition,
  PcgReviewApiContract,
  PcgReviewBundle,
  PcgReviewExport,
  PcgReviewExportResult,
  PcgReviewExportSummary,
} from "./pcg-review-types"

/**
 * Export POST failed the server's structural gate for the export shape in
 * the handoff's third-party evidence spec
 * (`pcg_exam_viewer_third_party_evidence_spec.md`) §13.3 (HTTP 422, nothing
 * stored). Carries the per-path error strings so the page can show WHAT was
 * structurally wrong rather than a generic failure.
 */
class PcgExportPayloadRejectedError extends Error {
  readonly schemaVersion: string
  readonly errors: string[]

  constructor(body: PcgExportValidationErrorBody) {
    super(body.message || "export payload failed §13.3 structural validation")
    // TS <4.6 / ES2015 target: restore the prototype chain for instanceof
    Object.setPrototypeOf(this, PcgExportPayloadRejectedError.prototype)
    this.name = "PcgExportPayloadRejectedError"
    this.schemaVersion = body.schemaVersion
    this.errors = body.errors || []
  }
}

const exportValidationBody = (err: unknown): PcgExportValidationErrorBody | null => {
  const response = (err as AxiosError<{ error?: PcgExportValidationErrorBody }>).response
  if (response?.status !== 422) {
    return null
  }
  // The house http_error_handler wraps FastAPI's `detail` as `error`.
  const body = response.data?.error
  return body && Array.isArray(body.errors) ? body : null
}

/**
 * Heart-report save lost the optimistic-concurrency race (HTTP 409, nothing
 * stored): another save landed since this edit session's base version. The
 * page should offer a reload, never silently clobber.
 */
class PcgHeartReportConflictError extends Error {
  readonly latestVersion: number | null
  readonly baseVersion: number | null

  constructor(body: PcgHeartReportConflictBody) {
    super(body.message || "the Medaica Report was saved by someone else since this edit session started")
    Object.setPrototypeOf(this, PcgHeartReportConflictError.prototype)
    this.name = "PcgHeartReportConflictError"
    this.latestVersion = body.latestVersion ?? null
    this.baseVersion = body.baseVersion ?? null
  }
}

const heartReportConflictBody = (err: unknown): PcgHeartReportConflictBody | null => {
  const response = (err as AxiosError<{ error?: PcgHeartReportConflictBody }>).response
  if (response?.status !== 409) {
    return null
  }
  const body = response.data?.error
  return body && "latestVersion" in body ? body : null
}

/**
 * Patient-report PDF refused (HTTP 409): the latest Medaica Report is not
 * released, so there is nothing to hand to the patient yet.
 */
class PcgPatientReportNotReleasedError extends Error {
  constructor() {
    super("Medaica Report is not released")
    Object.setPrototypeOf(this, PcgPatientReportNotReleasedError.prototype)
    this.name = "PcgPatientReportNotReleasedError"
  }
}

class PcgReviewApi implements PcgReviewApiContract {
  private readonly _apiService: MedaicaApiService

  constructor(apiService: MedaicaApiService) {
    this._apiService = apiService
  }

  /** GET /exams/{examId}/pcg-review — the single self-contained review load. */
  async getReviewBundle(exam: Exam): Promise<PcgReviewBundle> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<PcgReviewBundle>(`/exams/${exam.id}/pcg-review`)
    return response.data
  }

  /** PUT /exams/{examId}/auscultations/{auscultationId}/disposition (append-only, whole-state). */
  async putDisposition(
    examId: string,
    auscultationId: string,
    request: PcgDispositionRequest
  ): Promise<PcgRecordingDisposition> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.put<PcgRecordingDisposition>(
      `/exams/${examId}/auscultations/${auscultationId}/disposition`,
      request
    )
    return response.data
  }

  /**
   * POST /exams/{examId}/pcg-review/export — structural spec §13.3 gate, then
   * the client-built payload is stored verbatim; 201 returns the FULL stored row with
   * `created: true`. When the review has not changed since the latest
   * export the server returns that export (200, `created: false`) instead
   * of storing a duplicate. Throws PcgExportPayloadRejectedError (nothing
   * stored) on a 422.
   */
  async postExport(examId: string, request: PcgExportRequest): Promise<PcgReviewExportResult> {
    const apiClient = await this._apiService.apiClient()
    try {
      const response = await apiClient.post<PcgReviewExportResult>(`/exams/${examId}/pcg-review/export`, request)
      return response.data
    } catch (err) {
      const body = exportValidationBody(err)
      if (body) {
        throw new PcgExportPayloadRejectedError(body)
      }
      throw err
    }
  }

  /** GET /exams/{examId}/pcg-review/exports — summaries without the (large) payloads. */
  async getExports(examId: string): Promise<PcgReviewExportSummary[]> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<PcgReviewExportSummary[]>(`/exams/${examId}/pcg-review/exports`)
    return response.data
  }

  /** GET /exams/{examId}/pcg-review/exports/{version} — one export (by per-exam version) with its full payload. */
  async getExport(examId: string, version: number): Promise<PcgReviewExport> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<PcgReviewExport>(`/exams/${examId}/pcg-review/exports/${version}`)
    return response.data
  }

  /**
   * POST /exams/{examId}/heart-report — one Save = one new immutable version,
   * carrying its lifecycle status (saving "approved" marks the exam
   * reviewed; reopening with "in_review" clears that). Throws
   * PcgHeartReportConflictError on a stale baseVersion (409, nothing
   * stored); 201 returns the FULL stored row.
   */
  async saveHeartReport(examId: string, request: PcgHeartReportSaveRequest): Promise<PcgHeartReport> {
    const apiClient = await this._apiService.apiClient()
    try {
      const response = await apiClient.post<PcgHeartReport>(`/exams/${examId}/heart-report`, request)
      return response.data
    } catch (err) {
      const body = heartReportConflictBody(err)
      if (body) {
        throw new PcgHeartReportConflictError(body)
      }
      throw err
    }
  }

  /** GET /exams/{examId}/heart-report/versions — summaries without the (large) documents. */
  async getHeartReportVersions(examId: string): Promise<PcgHeartReportSummary[]> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<PcgHeartReportSummary[]>(`/exams/${examId}/heart-report/versions`)
    return response.data
  }

  /** GET /exams/{examId}/heart-report/versions/{version} — one saved report document. */
  async getHeartReportVersion(examId: string, version: number): Promise<PcgHeartReport> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<PcgHeartReport>(`/exams/${examId}/heart-report/versions/${version}`)
    return response.data
  }

  /**
   * GET /exams/{examId}/heart-report/patient-report.pdf — the frozen patient
   * artifact of the active release: the same bytes the patient downloads.
   * Throws PcgPatientReportNotReleasedError on a 409.
   */
  async getPatientReportPdf(examId: string): Promise<Blob> {
    const apiClient = await this._apiService.apiClient()
    try {
      const response = await apiClient.get<Blob>(`/exams/${examId}/heart-report/patient-report.pdf`, {
        responseType: "blob",
      })
      return response.data
    } catch (err) {
      if ((err as AxiosError).response?.status === 409) {
        throw new PcgPatientReportNotReleasedError()
      }
      throw err
    }
  }

  /**
   * GET /exams/{examId}/heart-report/provider-report.pdf — the frozen
   * provider report of the active release: the same bytes the patient
   * downloads to share with their own doctor. Throws
   * PcgPatientReportNotReleasedError on a 409; a release that carries no
   * provider report answers 404, which surfaces as the plain request error.
   */
  async getProviderReportPdf(examId: string): Promise<Blob> {
    const apiClient = await this._apiService.apiClient()
    try {
      const response = await apiClient.get<Blob>(`/exams/${examId}/heart-report/provider-report.pdf`, {
        responseType: "blob",
      })
      return response.data
    } catch (err) {
      if ((err as AxiosError).response?.status === 409) {
        throw new PcgPatientReportNotReleasedError()
      }
      throw err
    }
  }

  /**
   * POST /exams/{examId}/heart-report/resend-email — queue the report-ready
   * email again with a fresh link (the earlier link dies). 409 → not
   * released (PcgPatientReportNotReleasedError); 429 (RATE_LIMITED) within a
   * minute of the last send; returns the refreshed release facts.
   */
  async resendPatientReportEmail(examId: string): Promise<PcgHeartReportRelease> {
    const apiClient = await this._apiService.apiClient()
    try {
      const response = await apiClient.post<PcgHeartReportRelease>(`/exams/${examId}/heart-report/resend-email`)
      return response.data
    } catch (err) {
      if ((err as AxiosError).response?.status === 409) {
        throw new PcgPatientReportNotReleasedError()
      }
      throw err
    }
  }

  /**
   * GET /pcg-review/models/{version} — a stored analysis-model artifact
   * (immutable per version, served with a long-lived cache header). Returned
   * verbatim; the artifact declares no date-like strings, so the client's
   * date-reviver interceptor leaves it untouched.
   */
  async getModelArtifact(version: string): Promise<Record<string, unknown>> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<Record<string, unknown>>(`/pcg-review/models/${encodeURIComponent(version)}`)
    return response.data
  }
}

export default PcgReviewApi
export { PcgExportPayloadRejectedError, PcgHeartReportConflictError, PcgPatientReportNotReleasedError }
